import { LinearGradient } from 'expo-linear-gradient';
import { Check, Sparkles, Zap } from 'lucide-react-native';
import type { ComponentType } from 'react';
import { Platform, ScrollView, StyleSheet, useWindowDimensions, View } from 'react-native';
import { useTranslation } from '../../contexts/TranslationContext';
import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';
import { tokens } from '../../theme/tokens';
import { darkPrecisionFontFamilies } from '../../theme/typography';
import { ScrollReveal } from '../animations';
import { Badge } from '../primitives/Badge';
import { Container } from '../primitives/Container';
import { Text } from '../primitives/Text';
import { LandingSectionHeading } from './LandingSectionHeading';
import {
  GLASS_GRADIENT,
  GLASS_GRADIENT_END,
  GLASS_GRADIENT_START,
  WARNING_GRADIENT,
} from './landingStyleKit';

const STEP_META: {
  key: 'capture' | 'nurture' | 'close';
  icon: ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;
}[] = [
  { key: 'capture', icon: Sparkles },
  { key: 'nurture', icon: Zap },
  { key: 'close', icon: Check },
];

const StepIconWell = ({
  icon: Icon,
  human,
}: {
  icon: ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;
  human: boolean;
}) => (
  <View
    style={[
      iconStyles.well,
      { borderColor: human ? colors.status.warning.border : colors.primary.border },
    ]}
  >
    <LinearGradient
      colors={human ? WARNING_GRADIENT : colors.metrics.gradients.visitors}
      start={GLASS_GRADIENT_START}
      end={GLASS_GRADIENT_END}
      style={[StyleSheet.absoluteFill, { borderRadius: 14 }]}
    />
    <Icon
      size={23}
      color={human ? colors.status.warning.light : colors.primary[300]}
      strokeWidth={2}
    />
  </View>
);

interface StageCardProps {
  index: number;
  stage: string;
  automation: string;
  description: string;
  isHuman: boolean;
  icon: ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;
  isDesktop: boolean;
  cardWidth: number;
}

const StageCard = ({
  index,
  stage,
  automation,
  description,
  isHuman,
  icon,
  isDesktop,
  cardWidth,
}: StageCardProps) => {
  const { t } = useTranslation();
  return (
    <View
      style={[
        cardStyles.card,
        isDesktop ? cardStyles.cardDesktop : { width: cardWidth },
        {
          borderColor: isHuman ? colors.status.warning.border : colors.primary.borderActive,
        },
      ]}
    >
      <LinearGradient
        colors={isHuman ? WARNING_GRADIENT : GLASS_GRADIENT}
        start={GLASS_GRADIENT_START}
        end={GLASS_GRADIENT_END}
        style={StyleSheet.absoluteFill}
      />
      <View style={cardStyles.body}>
        <View style={cardStyles.topRow}>
          <Text style={cardStyles.stepNumber}>{`0${index + 1}`}</Text>
          <Badge
            color={isHuman ? 'amber' : 'cyan'}
            variant={isHuman ? 'subtle' : 'filled'}
            size="sm"
          >
            {isHuman
              ? t('components.landing.humanTouch')
              : `${automation} ${t('components.landing.automated')}`}
          </Badge>
        </View>
        <StepIconWell icon={icon} human={isHuman} />
        <View style={cardStyles.copy}>
          <Text variant="h4" aria-level={3} style={cardStyles.stageName}>
            {stage}
          </Text>
          <Text variant="bodySmall" color={colors.text.secondary} style={cardStyles.description}>
            {description}
          </Text>
        </View>
      </View>
    </View>
  );
};

export const AutomationShowcase = () => {
  const { t, getTranslation } = useTranslation();
  const { width } = useWindowDimensions();
  const isDesktop = width >= tokens.breakpoints.desktop;
  const cardWidth = Platform.select({ web: 300, default: 260 });

  const steps = STEP_META.map((meta) => ({
    icon: meta.icon,
    stage: t(`landing.automation.steps.${meta.key}.stage`),
    automation: t(`landing.automation.steps.${meta.key}.automation`),
    description: t(`landing.automation.steps.${meta.key}.description`),
    isHuman: getTranslation<boolean>(`landing.automation.steps.${meta.key}.isHuman`) || false,
  }));

  return (
    <View style={styles.section}>
      <Container>
        <ScrollReveal style={styles.header}>
          <LandingSectionHeading
            eyebrow={t('landing.automation.badge')}
            title={t('landing.automation.title')}
            accent={t('landing.automation.accent')}
            subtitle={t('landing.automation.subtitle')}
          />
        </ScrollReveal>

        {isDesktop ? (
          <View style={styles.grid}>
            {steps.map((step, index) => (
              <StageCard key={step.stage} index={index} {...step} isDesktop cardWidth={cardWidth} />
            ))}
          </View>
        ) : (
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.scroll}
          >
            {steps.map((step, index) => (
              <StageCard
                key={step.stage}
                index={index}
                {...step}
                isDesktop={false}
                cardWidth={cardWidth}
              />
            ))}
          </ScrollView>
        )}
      </Container>
    </View>
  );
};

const styles = StyleSheet.create({
  section: {
    backgroundColor: colors.transparent,
    paddingVertical: Platform.select({ web: 96, default: 64 }),
  },
  header: {
    alignItems: 'center',
    marginBottom: Platform.select({ web: 48, default: 32 }),
  },
  grid: {
    flexDirection: 'row',
    gap: spacing.lg,
    maxWidth: 1080,
    alignSelf: 'center',
    width: '100%',
  },
  scroll: {
    flexDirection: 'row',
    gap: spacing.lg,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
  },
});

const cardStyles = StyleSheet.create({
  card: {
    borderRadius: tokens.radius.xl,
    borderWidth: 1,
    overflow: 'hidden',
  },
  cardDesktop: {
    flex: 1,
    minWidth: 0,
  },
  body: {
    padding: Platform.select({ web: 26, default: 22 }),
    gap: spacing.lg,
  },
  topRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  stepNumber: {
    fontFamily: darkPrecisionFontFamilies.metric,
    fontSize: 13,
    color: colors.text.tertiary,
  },
  copy: {
    gap: spacing.xs,
  },
  stageName: {
    fontSize: Platform.select({ web: 20, default: 18 }),
    fontWeight: '600',
    color: colors.text.primary,
  },
  description: {
    lineHeight: 22,
  },
});

const iconStyles = StyleSheet.create({
  well: {
    width: 52,
    height: 52,
    borderRadius: 14,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
});
